import type { PageNodeBody } from '@/shared/api/generated/model';

/**
 * Дерево страниц из плоского списка сервера. Сервер отдаёт только видимые
 * страницы, уже упорядоченные внутри уровня; здесь — сборка по parent_id,
 * развёртка в список с глубиной для отрисовки и проекция для
 * перетаскивания.
 */

export interface PageNode extends PageNodeBody {
  children: PageNode[];
}

export interface FlatPage extends PageNodeBody {
  depth: number;
  hasChildren: boolean;
}

/** Отступ одного уровня вложенности в пикселях — общий для вида и dnd. */
export const INDENT = 18;

export function buildTree(items: readonly PageNodeBody[]): PageNode[] {
  const byID = new Map<string, PageNode>();

  for (const item of items) {
    byID.set(item.id, { ...item, children: [] });
  }

  const roots: PageNode[] = [];

  for (const item of items) {
    const node = byID.get(item.id);

    if (!node) {
      continue;
    }

    const parent = item.parent_id === null ? undefined : byID.get(item.parent_id);

    // Родителя нет в списке (скрыт) — сервер такого не отдаёт, но на
    // всякий случай: показать на верхнем уровне, а не потерять.
    if (parent) {
      parent.children.push(node);
    } else {
      roots.push(node);
    }
  }

  return roots;
}

/** Развёртка дерева в список; свёрнутые ветки пропускаются. */
export function flatten(
  nodes: readonly PageNode[],
  collapsed: ReadonlySet<string>,
  depth = 0,
): FlatPage[] {
  return nodes.flatMap((node) => {
    const { children, ...rest } = node;
    const self: FlatPage = { ...rest, depth, hasChildren: children.length > 0 };

    return collapsed.has(node.id) ? [self] : [self, ...flatten(children, collapsed, depth + 1)];
  });
}

/** Все id внутри ветки, включая корень. */
export function subtreeIDs(nodes: readonly PageNode[], id: string): Set<string> {
  const out = new Set<string>();

  const walk = (node: PageNode) => {
    out.add(node.id);
    node.children.forEach(walk);
  };

  const find = (list: readonly PageNode[]): PageNode | undefined => {
    for (const node of list) {
      if (node.id === id) {
        return node;
      }

      const inner = find(node.children);

      if (inner) {
        return inner;
      }
    }

    return undefined;
  };

  const root = find(nodes);

  if (root) {
    walk(root);
  }

  return out;
}

/** Путь от корня до страницы — для хлебных крошек. */
export function ancestors(items: readonly PageNodeBody[], id: string): PageNodeBody[] {
  const byID = new Map(items.map((item) => [item.id, item]));
  const path: PageNodeBody[] = [];
  let cursor = byID.get(id)?.parent_id ?? null;

  while (cursor !== null && path.length < 100) {
    const parent = byID.get(cursor);

    if (!parent) {
      break;
    }

    path.unshift(parent);
    cursor = parent.parent_id;
  }

  return path;
}

export interface Projection {
  depth: number;
  parentID: string | null;
  afterID: string | null;
}

/**
 * Куда ляжет перетаскиваемая страница: список уже без её ветки, `over` —
 * индекс, на который её опустили, `dx` — насколько увели вправо. Глубина
 * — от горизонтального сдвига, но не глубже «внутрь предыдущей» и не
 * мельче следующей строки: иначе дерево порвётся. Родитель — ближайшая
 * сверху строка на уровень выше, сосед «после» — ближайшая сверху строка
 * той же глубины под тем же родителем.
 */
export function projectDrop(
  list: readonly FlatPage[],
  activeDepth: number,
  over: number,
  dx: number,
): Projection {
  const previous = list[over - 1];
  const next = list[over];
  const maxDepth = previous ? previous.depth + 1 : 0;
  const minDepth = next ? next.depth : 0;
  const wanted = activeDepth + Math.round(dx / INDENT);
  const depth = Math.max(minDepth, Math.min(maxDepth, wanted));

  let parentID: string | null = null;
  let afterID: string | null = null;

  for (let index = over - 1; index >= 0; index -= 1) {
    const item = list[index];

    if (!item) {
      continue;
    }

    if (item.depth === depth - 1) {
      parentID = item.id;
      break;
    }

    if (item.depth === depth && afterID === null) {
      afterID = item.id;
      parentID = item.parent_id;
      break;
    }
  }

  return { depth, parentID, afterID };
}
